import { addDays, isoWeek, mondayOf, planningDay, weekdayIndex, type PlanningMoment } from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { lineMeta, whenText } from "./PlanningGrid";
import { PlanningMark, STATUS_DOT } from "./PlanningMark";
import type { PlanRow } from "./planningView";

const WD = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const dayText = (iso: string) => `${WD[weekdayIndex(iso)]} ${planningDay(iso).slice(0, -5)}`;

interface Item {
  r: PlanRow;
  m: PlanningMoment;
}

/**
 * Telefoon: agenda per week in de gekozen periode, met verlopen werk bovenaan. Een afspraak staat op de geplande
 * dag; de vervaldatum dan niet nog een keer. Elke regel opent het venster (inplannen, verplaatsen, afronden).
 */
export function PlanningAgenda({ rows, onOpen }: { rows: PlanRow[]; onOpen: (r: PlanRow) => void }) {
  const overdue = rows.filter((r) => r.status === "verlopen");
  const weeks = new Map<string, Item[]>();
  for (const r of rows)
    for (const m of r.moments) {
      // Verlopen staat al bovenaan.
      if (m.type === "due" && (r.status === "verlopen" || r.l.appointment)) continue;
      const wk = mondayOf(m.date);
      weeks.set(wk, [...(weeks.get(wk) ?? []), { r, m }]);
    }
  const list = [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b));
  if (!overdue.length && !list.length) return <p className="muted">Niets gepland in deze periode.</p>;
  return (
    <div className="kp-agenda">
      {overdue.length > 0 && (
        <section className="kp-week" aria-label="Verlopen">
          <h3 className="label">Verlopen</h3>
          <ul className="list compact">
            {overdue.map((r) => (
              <li key={r.l.id}>
                <button type="button" className="linkrow kp-item" onClick={() => onOpen(r)}>
                  <i className={`obdot ${STATUS_DOT[r.status]}`} aria-hidden="true" />
                  <span className="grow">
                    <span className="kp-title block">{r.l.title}</span>
                    <Meta className="small muted" items={lineMeta(r, true)} />
                    <span className="small block kp-when kp-when-verlopen">{whenText(r)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {list.map(([monday, items]) => {
        const w = isoWeek(monday);
        const title = `Week ${w.week} · ${planningDay(monday).slice(0, -5)} – ${planningDay(addDays(monday, 6)).slice(0, -5)}`;
        items.sort((a, b) => a.m.date.localeCompare(b.m.date) || a.r.l.title.localeCompare(b.r.l.title, "nl"));
        return (
          <section key={monday} className="kp-week" aria-label={`Week ${w.week}`}>
            <h3 className="label">{title}</h3>
            <ul className="list compact">
              {items.map(({ r, m }) => (
                <li key={`${r.l.id}-${m.date}-${m.type}`}>
                  <button type="button" className="linkrow kp-item" onClick={() => onOpen(r)}>
                    <PlanningMark type={m.type} status={r.status} dates={[m.date]} />
                    <span className="grow">
                      <span className="kp-title block">{r.l.title}</span>
                      <Meta className="small muted" items={lineMeta(r, true)} />
                    </span>
                    <span className="small muted kp-day">{dayText(m.date)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
